import React, { createContext, useContext, useEffect, useState } from 'react'

import { useAdminApi } from '../hooks/useAdminApi'

import { useFirebase } from './FirebaseProvider'

export interface AdminUser {
  id: string // email
  name: string
  picture: string
  status: 'active' | 'inactive'
  permissions: string[]
  created_at: string
  updated_at: string
  last_login_at?: string
}

export type AdminPermission =
  | 'view_dashboard'
  | 'view_shops'
  | 'login_shops'
  | 'view_admin'
  | 'manage_admin'

interface AdminAuthContextType {
  adminUser: AdminUser | null
  loading: boolean
  error: string | null
  hasPermission: (permission: AdminPermission) => boolean
  refetchAdminData: () => Promise<void>
  clearError: () => void
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(
  undefined
)

export function useAdminAuth() {
  const context = useContext(AdminAuthContext)
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}

interface AdminAuthProviderProps {
  children: React.ReactNode
}

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
  const { user, signOut } = useFirebase()
  const { GetProfile } = useAdminApi()
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAdminData = async () => {
    if (!user) {
      setAdminUser(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const response = await GetProfile()
      if (response.success && response.data) {
        // Check if the account is active
        if (response.data.status !== 'active') {
          const errorMessage = `Your admin account (${response.data.id} - ${response.data.name}) is currently inactive. Please contact a system administrator to activate your account.`
          setError(errorMessage)
          setAdminUser(null)

          // Sign out the Firebase user for inactive accounts
          try {
            await signOut()
          } catch (signOutError) {
            console.error(
              'Error signing out after inactive account:',
              signOutError
            )
          }
          return
        }

        setAdminUser(response.data)
        setError(null)
      } else {
        console.error('Failed to verify admin access:', response.error)
        const errorMessage = response.error || 'Failed to verify admin access'
        setError(errorMessage)
        setAdminUser(null)

        // If the API returns an error, sign out the Firebase user
        // This handles cases like unauthorized access, expired tokens, etc.
        try {
          await signOut()
        } catch (signOutError) {
          console.error('Error signing out after API failure:', signOutError)
        }
      }
    } catch (error) {
      console.error('Error fetching admin data:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to verify admin access'
      setError(errorMessage)
      setAdminUser(null)

      // If there's a network error or other exception, also sign out
      try {
        await signOut()
      } catch (signOutError) {
        console.error('Error signing out after network error:', signOutError)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAdminData()
  }, [user])

  const hasPermission = (permission: AdminPermission): boolean => {
    if (!adminUser) {
      return false
    }
    return adminUser.permissions.includes(permission)
  }

  const clearError = () => {
    setError(null)
  }

  const value = {
    adminUser,
    loading,
    error,
    hasPermission,
    refetchAdminData: fetchAdminData,
    clearError,
  }

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}
